// mobile/screens/AddExpense.js
import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import axios from 'axios';
import { API_BASE } from '../utils';
import { Ionicons } from '@expo/vector-icons';
import EnterpriseHeader from '../components/EnterpriseHeader';
import { PADIPRO, padiproShadow, padiproShadowMd } from '../components/padiproTheme';
import { useResponsive } from '../components/responsive';

// Native date picker (Android/iOS)
import DateTimePicker from '@react-native-community/datetimepicker';
// JS-only date picker (works on Web)
import DatePicker from 'react-native-ui-datepicker';
import dayjs from 'dayjs';

const isWeb = Platform.OS === 'web';

const DEFAULT_CATEGORY_LIST = [
  { key: 'pestisida', label: 'pestisida' },
  { key: 'gaji', label: 'gaji karyawan' },
  { key: 'parbabo', label: 'parbabo' },
  { key: 'tukang_tanam', label: 'tukang tanam' },
  { key: 'rotari', label: 'rotari' },
  { key: 'jetor', label: 'jetor' },
  { key: 'bibit', label: 'bibit' },
  { key: 'lainnya', label: 'lainnya' },
  { key: 'warung', label: 'warung' },
  { key: 'tukang_semprot', label: 'tukang semprot' },
  { key: 'sewa_tanah', label: 'sewa tanah' },
  { key: 'manolotin', label: 'manolotin' },
  { key: 'gas', label: 'gas' },
  { key: 'pupuk', label: 'pupuk' },
  { key: 'ikat_padi', label: 'ikat padi' },
  { key: 'gaji_mupuk', label: 'gaji mupuk' },
  { key: 'gaji_gadu2', label: 'gaji gadu2' },
  { key: 'bikin_lapakbibit', label: 'bikin_lapakbibit' },
  { key: 'keperluaan_rumah', label: 'keperluan rumah' },
  { key: 'panen', label: 'panen' },
];

const PESTICIDE_SUBS = ['Insektisida', 'Herbisida', 'Fungisida', 'Moluskisida', 'Bakterisida'];
const PUPUK_SUBS = [
  { key: 'pupuk_semprot', label: 'Pupuk Semprot' },
  { key: 'urea', label: 'Urea' },
  { key: 'phoska', label: 'Phoska' },
  { key: 'phoska_plus', label: 'Phoska Plus' },
  { key: 'za', label: 'ZA' },
  { key: 'fertiphos', label: 'Fertiphos' },
  { key: 'tsp_tawon', label: 'TSP Tawon' },
  { key: 'dolomit', label: 'Dolomit' },
  { key: 'pupuk_lainnya', label: 'Lainnya' },
];
const COMPANY_PRESETS = ['Syngenta', 'Bayer', 'Corteva', 'BASF', 'FMC', 'Lainnya'];
const UNIT_PRESETS_PEST = ['botol', 'liter', 'ml', 'kg', 'gram'];

const CATEGORY_UNIT_PRESETS = {
  gaji: ['orang', 'hari', 'jam'],
  parbabo: ['rit', 'km', 'trip'],
  tukang_tanam: ['orang', 'hari'],
  rotari: ['jam', 'hari', 'unit'],
  jetor: ['jam', 'hari', 'unit'],
  bibit: ['kg', 'butir', 'karung'],
  lainnya: ['unit', 'pcs', 'paket'],
  warung: ['pcs', 'paket', 'unit'],
  tukang_semprot: ['orang', 'hari'],
  sewa_tanah: ['ha', 'rante', 'm2'],
  manolotin: ['orang', 'hari'],
  gas: ['tabung', 'liter'],
  pupuk: ['kg', 'sak'],
  ikat_padi: ['orang'],
  gaji_mupuk: ['orang', 'hari'],
  gaji_gadu2: ['orang', 'hari'],
  bikin_lapakbibit: ['orang', 'hari'],
  keperluaan_rumah: ['unit', 'pcs', 'paket'],
  panen: ['orang', 'hari', 'karung'],
};

const NAME_PLACEHOLDER = {
  pestisida: 'Decis 25 EC',
  gaji: 'Gaji Karyawan / Lembur',
  parbabo: 'Biaya angkut / sewa truk',
  tukang_tanam: 'Upah tanam / borongan',
  rotari: 'Sewa rotari',
  jetor: 'Sewa jetor',
  bibit: 'Benih padi / bibit jagung',
  lainnya: 'Deskripsi pengeluaran',
  warung: 'Belanja kebutuhan warung',
  tukang_semprot: 'Upah tukang semprot',
  sewa_tanah: 'Sewa lahan / tanah',
  manolotin: 'Upah manolotin padi',
  gas: 'Tabung gas / isi ulang',
  pupuk: 'Pupuk urea / NPK',
  ikat_padi: 'Biaya ikat padi',
  gaji_mupuk: 'Upah tenaga mupuk',
  gaji_gadu2: 'Upah tenaga gadu2',
  bikin_lapakbibit: 'Upah tenaga bikin_lapakbibit',
  keperluaan_rumah: 'Belanja kebutuhan rumah',
  panen: 'Biaya panen / tenaga panen',
};

const COLORS = {
  bg: PADIPRO.bg,
  card: PADIPRO.surface,
  border: PADIPRO.border,
  text: PADIPRO.text,
  muted: PADIPRO.muted,
  accent: PADIPRO.primary,
  accentSoft: PADIPRO.primarySoft,
  dangerSoft: PADIPRO.dangerSoft,
  danger: PADIPRO.danger,
};
const R = { pad: 16, radius: 14 };

const formatIDR = (n) => {
  const v = Number(n || 0);
  return 'Rp ' + v.toLocaleString('id-ID');
};
const formatRante = (n) => Number(n || 0).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatSignedIDR = (n) => {
  const v = Number(n || 0);
  const sign = v > 0 ? '+' : v < 0 ? '-' : '';
  return `${sign}Rp ${Math.abs(v).toLocaleString('id-ID')}`;
};

const buildAllocationPreview = ({ total, method, fields }) => {
  const totalVal = Number(total || 0);
  const list = Array.isArray(fields) ? fields : [];
  const count = list.length;
  if (!(totalVal > 0) || count === 0) {
    return {
      rows: [],
      methodUsed: method === 'equal' ? 'equal' : 'area',
      denominator: 0,
      unitCost: 0,
      invalidAreaCount: 0,
      fallbackEqual: false,
      totalAllocated: 0,
    };
  }

  if (method === 'equal') {
    const each = totalVal / count;
    return {
      rows: list.map((f) => ({ id: f.id, name: f.name, area: Number(f.area_rante || 0), amount: each })),
      methodUsed: 'equal',
      denominator: count,
      unitCost: each,
      invalidAreaCount: 0,
      fallbackEqual: false,
      totalAllocated: each * count,
    };
  }

  const invalidAreaCount = list.filter((f) => !(Number(f.area_rante || 0) > 0)).length;
  if (invalidAreaCount > 0) {
    const each = totalVal / count;
    return {
      rows: list.map((f) => ({ id: f.id, name: f.name, area: Number(f.area_rante || 0), amount: each, note: 'rante kosong' })),
      methodUsed: 'equal',
      denominator: count,
      unitCost: each,
      invalidAreaCount,
      fallbackEqual: true,
      totalAllocated: each * count,
    };
  }

  const sumArea = list.reduce((acc, f) => acc + Number(f.area_rante || 0), 0);
  if (!(sumArea > 0)) {
    const each = totalVal / count;
    return {
      rows: list.map((f) => ({ id: f.id, name: f.name, area: Number(f.area_rante || 0), amount: each })),
      methodUsed: 'equal',
      denominator: count,
      unitCost: each,
      invalidAreaCount: 0,
      fallbackEqual: true,
      totalAllocated: each * count,
    };
  }

  const unitCost = totalVal / sumArea;
  return {
    rows: list.map((f) => {
      const area = Number(f.area_rante || 0);
      return { id: f.id, name: f.name, area, amount: unitCost * area };
    }),
    methodUsed: 'area',
    denominator: sumArea,
    unitCost,
    invalidAreaCount: 0,
    fallbackEqual: false,
    totalAllocated: totalVal,
  };
};

export default function AddExpense({ route, navigation }) {
  const token = route?.params?.token;
  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const { bottomInset, contentPadding, contentWidthStyle, isWide } = useResponsive();

  // ===== Date =====
  const [date, setDate] = useState('');
  const [dateObj, setDateObj] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // ===== Expense base =====
  const [categoryOptions, setCategoryOptions] = useState(DEFAULT_CATEGORY_LIST);
  const [category, setCategory] = useState('pestisida');
  const [subcategory, setSubcategory] = useState('Insektisida');
  const [categoryExpanded, setCategoryExpanded] = useState(true);
  const [categoryQuery, setCategoryQuery] = useState('');
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  const [newCategoryKey, setNewCategoryKey] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [pesticideMetaExpanded, setPesticideMetaExpanded] = useState(true);

  const [itemName, setItemName] = useState('');
  const [company, setCompany] = useState('');
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('');
  const [price, setPrice] = useState('');
  const [total, setTotal] = useState('');
  const [field, setField] = useState(''); // legacy manual field_name
  const [notes, setNotes] = useState('');

  const [seasonInfo, setSeasonInfo] = useState(null);
  const [forceAssign, setForceAssign] = useState(false);
  const [loading, setLoading] = useState(false);

  // ===== NEW: allocation =====
  const [assignMode, setAssignMode] = useState('season'); // 'season' | 'fields'
  const [fieldsList, setFieldsList] = useState([]); // [{id,name,area_rante}]
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]); // [id]
  const [fieldQuery, setFieldQuery] = useState('');
  const [splitMethod, setSplitMethod] = useState('area'); // 'area' | 'equal'

  // ===== pesticide picker =====
  const [selectedPesticide, setSelectedPesticide] = useState(null); // { pesticide_id, name, unit, category, company, unit_price }
  const [pestQuery, setPestQuery] = useState('');
  const [pestResults, setPestResults] = useState([]);
  const [pestLoading, setPestLoading] = useState(false);
  const [pestError, setPestError] = useState(null);
  const debounceRef = useRef(null);
  const lastSearchSeqRef = useRef(0);

  const loadExpenseCategories = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API_BASE}/expenses/categories`, { headers });
      const arr = Array.isArray(r?.data?.items) ? r.data.items : [];
      const mapped = arr
        .map((it) => ({
          key: String(it?.key || '').trim(),
          label: String(it?.label || it?.key || '').trim(),
        }))
        .filter((it) => it.key && it.label);

      if (mapped.length) {
        setCategoryOptions(mapped);
      } else {
        setCategoryOptions(DEFAULT_CATEGORY_LIST);
      }
    } catch {
      setCategoryOptions(DEFAULT_CATEGORY_LIST);
    }
  }, [headers, token]);

  const formatYMD = (d) => {
    if (!d) return '';
    try {
      const yy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    } catch {
      return '';
    }
  };

  // season marker
  useEffect(() => {
    const ymd = date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
    const url = ymd ? `${API_BASE}/seasons/current?date=${encodeURIComponent(ymd)}` : `${API_BASE}/seasons/current`;
    axios
      .get(url, { headers })
      .then((r) => setSeasonInfo(r.data))
      .catch(() => setSeasonInfo(null));
  }, [date, headers]);

  // sync dateObj from text
  useEffect(() => {
    if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const parts = date.split('-').map(Number);
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      if (!Number.isNaN(d.getTime())) setDateObj(d);
    }
  }, [date]);

  // fetch fields for allocation (compat)
  const normalizeFields = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((x) => {
        const id = x.id ?? x.field_id ?? x.fieldId ?? null;
        const name = x.name ?? x.field_name ?? x.title ?? '';
        const area = x.area_rante ?? x.rante ?? x.luas_rante ?? x.rante_size ?? null;
        return {
          id: id != null ? Number(id) : null,
          name: String(name || ''),
          area_rante: area != null ? Number(area) : null,
        };
      })
      .filter((x) => x.id && x.name);
  };

  const fetchFieldsCompat = useCallback(async () => {
    if (!token) return;
    setFieldsLoading(true);
    try {
      // 1) Primary source: all active fields (including pre-planting)
      const rMin = await axios.get(`${API_BASE}/operations/fields-min`, { headers });
      const rowsMin = rMin?.data?.fields || rMin?.data?.items || rMin?.data || [];
      const normMin = normalizeFields(rowsMin);
      if (normMin.length) {
        setFieldsList(normMin);
        return;
      }

      // 2) Fallback: operations list (existing behavior)
      const rOps = await axios.get(`${API_BASE}/operations/fields`, { headers });
      const rowsOps = rOps?.data?.fields || rOps?.data?.items || rOps?.data || [];
      const normOps = normalizeFields(rowsOps);
      if (normOps.length) {
        setFieldsList(normOps);
        return;
      }

      // 3) Legacy fallback (if available in older backend)
      const rLegacy = await axios.get(`${API_BASE}/fields`, { headers });
      const rowsLegacy = rLegacy?.data?.fields || rLegacy?.data?.items || rLegacy?.data || [];
      setFieldsList(normalizeFields(rowsLegacy));
    } catch {
      setFieldsList([]);
    } finally {
      setFieldsLoading(false);
    }
  }, [headers, token]);

  useEffect(() => {
    fetchFieldsCompat();
  }, [fetchFieldsCompat]);

  useEffect(() => {
    loadExpenseCategories();
  }, [loadExpenseCategories]);

  const isPesticide = category === 'pestisida';
  const isPupuk = category === 'pupuk';
  const usesSubcategory = isPesticide || isPupuk;

  const unitPresets = useMemo(() => {
    if (isPesticide) return UNIT_PRESETS_PEST;
    return CATEGORY_UNIT_PRESETS[category] || CATEGORY_UNIT_PRESETS.lainnya;
  }, [category, isPesticide]);

  const itemPlaceholder = NAME_PLACEHOLDER[category] || NAME_PLACEHOLDER.lainnya;

  const computedTotal = useMemo(() => {
    const q = Number(qty);
    const p = Number(price);
    if (!Number.isFinite(q) || !Number.isFinite(p)) return '';
    const t = q * p;
    return Number.isFinite(t) ? String(t) : '';
  }, [qty, price]);

  const finalTotalValue = useMemo(() => {
    const t = total?.trim() ? Number(total) : computedTotal?.trim() ? Number(computedTotal) : null;
    return Number.isFinite(t) ? t : null;
  }, [total, computedTotal]);

  const selectedCategoryLabel = useMemo(
    () => categoryOptions.find((c) => c.key === category)?.label || category,
    [category, categoryOptions]
  );
  const filteredCategories = useMemo(() => {
    const q = (categoryQuery || '').trim().toLowerCase();
    if (!q) return categoryOptions;
    return categoryOptions.filter(
      (c) => c.label.toLowerCase().includes(q) || c.key.toLowerCase().includes(q)
    );
  }, [categoryQuery, categoryOptions]);
  const visibleCategories = useMemo(() => {
    if (showAllCategories) return filteredCategories;
    if ((categoryQuery || '').trim()) return filteredCategories;
    return filteredCategories.slice(0, 8);
  }, [filteredCategories, showAllCategories, categoryQuery]);
  const hasHiddenCategories = useMemo(() => {
    if ((categoryQuery || '').trim()) return false;
    return filteredCategories.length > 8 && !showAllCategories;
  }, [filteredCategories, showAllCategories, categoryQuery]);

  const Chip = ({ active, children, onPress, style, textStyle }) => (
    <TouchableOpacity onPress={onPress} activeOpacity={0.9} style={[s.chip, style, active && s.chipActive]}>
      <Text style={[s.chipText, textStyle, active && s.chipTextActive]}>{children}</Text>
    </TouchableOpacity>
  );

  // ===== pesticide picker helpers =====
  const resetPesticidePicker = useCallback(() => {
    setSelectedPesticide(null);
    setPestQuery('');
    setPestResults([]);
    setPestError(null);
    setPestLoading(false);
  }, []);

  const onChangeCategory = (next) => {
    setCategory(next);

    if (next === 'pupuk') {
      setSubcategory('');
      setCompany('');
      resetPesticidePicker();
      setPesticideMetaExpanded(false);
    } else if (next !== 'pestisida') {
      setSubcategory('');
      setCompany('');
      resetPesticidePicker();
      setPesticideMetaExpanded(false);
    } else {
      setSubcategory('Insektisida');
      setPesticideMetaExpanded(true);
    }

    const presets = next === 'pestisida' ? UNIT_PRESETS_PEST : CATEGORY_UNIT_PRESETS[next] || [];
    setUnit(presets[0] || '');
  };

  const onPickCategory = (next) => {
    onChangeCategory(next);
    setCategoryExpanded(false);
    setCategoryQuery('');
    setShowAllCategories(false);
  };

  const createCategory = async () => {
    if (!token) return;
    const label = String(newCategoryLabel || '').trim();
    const key = String(newCategoryKey || '').trim();
    if (!label) return Alert.alert('Validasi', 'Nama kategori baru wajib diisi.');

    try {
      setCreatingCategory(true);
      const r = await axios.post(
        `${API_BASE}/expenses/categories`,
        { label, key: key || null },
        { headers }
      );
      const created = r?.data?.item;
      await loadExpenseCategories();
      if (created?.key) onPickCategory(created.key);
      setNewCategoryLabel('');
      setNewCategoryKey('');
      Alert.alert('Sukses', 'Kategori baru berhasil ditambahkan.');
    } catch (e) {
      Alert.alert('Error', e?.response?.data?.error || e?.message || 'Gagal menambah kategori');
    } finally {
      setCreatingCategory(false);
    }
  };

  const awaitGetActiveSeasonId = async () => {
    try {
      const r = await axios.get(`${API_BASE}/seasons/current`, { headers });
      return r?.data?.season_id || null;
    } catch {
      return null;
    }
  };

  const normalizePesticideRows = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((x) => ({
        pesticide_id: x.pesticide_id ?? x.id ?? x.pesticideId ?? null,
        name: x.name ?? x.item_name ?? x.title ?? '',
        unit: x.unit ?? null,
        category: x.category ?? x.subcategory ?? null,
        company: x.company ?? null,
        unit_price: x.unit_price ?? x.price ?? null,
        stock: x.stock ?? null,
      }))
      .filter((x) => x.pesticide_id && x.name);
  };

  // GET /pesticides/search?q=bes&limit=10
  const searchPesticides = useCallback(
    async (q) => {
      const query = String(q || '').trim();

      if (!query || query.length < 2) {
        setPestResults([]);
        setPestError(null);
        setPestLoading(false);
        return;
      }
      if (!token) return;

      const mySeq = ++lastSearchSeqRef.current;

      setPestLoading(true);
      setPestError(null);

      try {
        const r = await axios.get(`${API_BASE}/pesticides/search`, {
          headers,
          params: { q: query, limit: 10 },
        });

        const rows = r?.data?.items || [];
        const norm = normalizePesticideRows(rows);

        if (mySeq !== lastSearchSeqRef.current) return;

        setPestResults(norm.slice(0, 10));
        if (norm.length === 0) setPestError('Produk tidak ditemukan');
      } catch (e) {
        if (mySeq !== lastSearchSeqRef.current) return;
        setPestResults([]);
        setPestError(e?.response?.data?.error || e?.message || 'Gagal cari produk');
      } finally {
        if (mySeq === lastSearchSeqRef.current) setPestLoading(false);
      }
    },
    [headers, token]
  );

  useEffect(() => {
    if (!isPesticide) return;
    if (selectedPesticide) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchPesticides(pestQuery);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [pestQuery, isPesticide, selectedPesticide, searchPesticides]);

  const selectPesticide = (p) => {
    setSelectedPesticide(p);

    setItemName(p.name);
    if (p.unit && !unit) setUnit(p.unit);
    if (p.category && !subcategory) setSubcategory(String(p.category));
    if (p.company) setCompany(p.company);

    if ((!price || String(price).trim() === '') && p.unit_price != null) {
      setPrice(String(p.unit_price));
    }

    setPestResults([]);
    setPestQuery('');
    setPestError(null);
  };

  const clearSelectedPesticide = () => {
    setSelectedPesticide(null);
  };

  // ===== allocation helpers =====
  const toggleFieldPick = (id) => {
    const fid = Number(id);
    if (!fid) return;
    setSelectedFields((prev) => (prev.includes(fid) ? prev.filter((x) => x !== fid) : [...prev, fid]));
  };

  const selectedFieldObjs = useMemo(() => {
    const map = new Map(fieldsList.map((f) => [f.id, f]));
    return selectedFields.map((id) => map.get(id)).filter(Boolean);
  }, [fieldsList, selectedFields]);
  const filteredFieldsList = useMemo(() => {
    const q = String(fieldQuery || '').trim().toLowerCase();
    if (!q) return fieldsList;
    return fieldsList.filter((f) => {
      const hay = `${f.name || ''} ${f.area_rante || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [fieldsList, fieldQuery]);
  const selectedAreaTotal = useMemo(
    () => selectedFieldObjs.reduce((acc, f) => acc + Number(f.area_rante || 0), 0),
    [selectedFieldObjs]
  );
  const selectAllFilteredFields = useCallback(() => {
    setSelectedFields(filteredFieldsList.map((f) => Number(f.id)).filter(Boolean));
  }, [filteredFieldsList]);
  const clearSelectedFields = useCallback(() => {
    setSelectedFields([]);
  }, []);

  const splitPreview = useMemo(() => {
    if (assignMode !== 'fields') {
      return buildAllocationPreview({ total: 0, method: splitMethod, fields: [] });
    }
    return buildAllocationPreview({
      total: finalTotalValue || 0,
      method: splitMethod,
      fields: selectedFieldObjs,
    });
  }, [finalTotalValue, assignMode, splitMethod, selectedFieldObjs]);

  // ===== submit =====
  const submit = async () => {
    if (!token) return Alert.alert('Error', 'Token tidak ditemukan. Silakan login ulang.');
    if (!category) return Alert.alert('Validasi', 'Kategori wajib diisi');

    const hasSelected = !!selectedPesticide?.pesticide_id;
    const hasItemName = !!itemName.trim();
    if (!hasSelected && !hasItemName) {
      return Alert.alert('Validasi', 'Nama Item/Deskripsi wajib diisi (atau pilih produk)');
    }

    if (qty && !/^\d+(\.\d+)?$/.test(qty)) return Alert.alert('Validasi', 'Qty harus angka');
    if (price && !/^\d+(\.\d+)?$/.test(price)) return Alert.alert('Validasi', 'Harga per unit harus angka');
    if (total && !/^\d+(\.\d+)?$/.test(total)) return Alert.alert('Validasi', 'Total harus angka');

    const finalTotal = finalTotalValue;
    if (finalTotal == null || finalTotal <= 0) {
      return Alert.alert('Validasi', 'Total wajib diisi. Bisa isi total atau isi qty + harga.');
    }

    const extraNote = isPesticide && company ? `Company: ${company}` : '';
    const joinedNotes = [extraNote, notes].filter(Boolean).join(' | ') || null;

    // if user chooses allocation to fields
    if (assignMode === 'fields') {
      if (selectedFields.length === 0) {
        return Alert.alert('Validasi', 'Pilih minimal 1 lahan.');
      }

      const payloadSplit = {
        expense_date: date || null,
        category,
        subcategory: usesSubcategory ? subcategory || null : null,
        item_name: hasItemName ? itemName.trim() : null,
        quantity: qty ? Number(qty) : null,
        unit: unit || null,
        unit_price: price ? Number(price) : null,
        total: finalTotal,
        notes: joinedNotes,
        field_ids: selectedFields,
        split_method: splitMethod, // 'area' | 'equal'
      };

      if (isPesticide && selectedPesticide?.pesticide_id) {
        payloadSplit.pesticide_id = selectedPesticide.pesticide_id;
      }

      try {
        setLoading(true);
        await axios.post(`${API_BASE}/field-expenses/split`, payloadSplit, { headers });
        Alert.alert('Sukses', 'Biaya tersimpan dan dibagi ke lahan.');
        navigation.goBack();
      } catch (e) {
        Alert.alert('Error', e?.response?.data?.error || e?.message || 'Gagal menyimpan');
      } finally {
        setLoading(false);
      }
      return;
    }

    // season/legacy expense
    let allocSeasonId = null;
    if (!seasonInfo && forceAssign) {
      allocSeasonId = await awaitGetActiveSeasonId();
    }

    const payload = {
      expense_date: date || null,
      category,
      subcategory: usesSubcategory ? subcategory || null : null,
      item_name: hasItemName ? itemName.trim() : null,
      quantity: qty ? Number(qty) : null,
      unit: unit || null,
      unit_price: price ? Number(price) : null,
      total: finalTotal,
      field_name: field || null,
      notes: joinedNotes,
      allocated_season_id: allocSeasonId,
    };

    if (isPesticide && selectedPesticide?.pesticide_id) {
      payload.pesticide_id = selectedPesticide.pesticide_id;
    }

    try {
      setLoading(true);
      await axios.post(`${API_BASE}/expenses`, payload, { headers });
      Alert.alert('Sukses', 'Pengeluaran tersimpan');
      navigation.goBack();
    } catch (e) {
      Alert.alert('Error', e?.response?.data?.error || e?.message || 'Gagal menyimpan');
    } finally {
      setLoading(false);
    }
  };

  const outOfSeason = !seasonInfo;
  const seasonName = seasonInfo?.name || null;
  const assignModeLabel = assignMode === 'fields' ? 'Per Lahan' : 'Umum Musim';

  const openDatePicker = () => setShowDatePicker((v) => !v);

  const onNativeDateChange = (event, selectedDate) => {
    if (Platform.OS !== 'ios') setShowDatePicker(false);
    if (selectedDate) {
      setDateObj(selectedDate);
      setDate(formatYMD(selectedDate));
    }
  };

  return (
    <View style={s.root}>
      <View style={[s.screenBody, contentWidthStyle]}>
        <ScrollView
          style={s.formScroll}
          contentContainerStyle={[
            s.pageContent,
            { paddingHorizontal: contentPadding, paddingBottom: 112 + bottomInset },
          ]}
          contentInsetAdjustmentBehavior="automatic"
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
        <View style={s.headerWrap}>
          <EnterpriseHeader
            title="Tambah Pengeluaran"
            subtitle="Catat biaya operasional sawah secara rapi dan terukur"
          />
        </View>

        <View style={[s.infoBox, outOfSeason ? s.warnBox : s.okBox]}>
          <Ionicons name={outOfSeason ? 'alert-circle-outline' : 'leaf-outline'} size={18} color={outOfSeason ? PADIPRO.warning : COLORS.accent} />
          <Text style={[s.infoText, { flex: 1 }]}>{outOfSeason ? 'Tanggal ini di luar rentang musim' : `Masuk: ${seasonName}`}</Text>
          {outOfSeason && (
            <TouchableOpacity onPress={() => setForceAssign((v) => !v)} style={[s.forceBtn, forceAssign && { backgroundColor: COLORS.accent }]}>
              <Text style={[s.forceBtnText, forceAssign && { color: '#fff' }]}>Bebankan ke Musim Aktif</Text>
            </TouchableOpacity>
          )}
        </View>

        <View style={s.summaryStrip}>
          <View style={s.summaryItem}>
            <Text style={s.summaryLabel}>Kategori</Text>
            <Text numberOfLines={1} style={s.summaryValue}>{selectedCategoryLabel}</Text>
          </View>
          <View style={s.summaryDivider} />
          <View style={s.summaryItem}>
            <Text style={s.summaryLabel}>Mode</Text>
            <Text numberOfLines={1} style={s.summaryValue}>{assignModeLabel}</Text>
          </View>
          <View style={s.summaryDivider} />
          <View style={s.summaryItem}>
            <Text style={s.summaryLabel}>Total</Text>
            <Text numberOfLines={1} style={s.summaryValue}>{formatIDR(finalTotalValue || 0)}</Text>
          </View>
        </View>

        <View style={s.sectionCard}>
          <View style={s.sectionTitleRow}>
            <View style={s.sectionIconCircle}>
              <Ionicons name="pricetag-outline" size={16} color="#14532d" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.sectionStep}>Langkah 1</Text>
              <Text style={s.sectionTitle}>Kategori & Identitas Biaya</Text>
              <Text style={s.sectionDesc}>Pilih tipe pengeluaran agar input berikutnya lebih akurat.</Text>
            </View>
          </View>

          <View style={s.categoryTopBar}>
            <View style={s.selectionBadge}>
              <Text style={s.selectionBadgeText}>Dipilih: {selectedCategoryLabel}</Text>
            </View>
            <TouchableOpacity
              onPress={() => setCategoryExpanded((v) => !v)}
              activeOpacity={0.85}
              style={s.compactToggleBtn}
            >
              <Text style={s.compactToggleText}>{categoryExpanded ? 'Sembunyikan' : 'Ubah Kategori'}</Text>
              <Ionicons name={categoryExpanded ? 'chevron-up' : 'chevron-down'} size={15} color="#166534" />
            </TouchableOpacity>
          </View>

          {categoryExpanded && (
            <>
              <Text style={s.label}>Pilih Kategori</Text>
              <View style={s.searchInputWrap}>
                <Ionicons name="search-outline" size={17} color={COLORS.muted} />
                <TextInput
                  value={categoryQuery}
                  onChangeText={setCategoryQuery}
                  placeholder="Cari kategori..."
                  placeholderTextColor={COLORS.muted}
                  style={{ flex: 1, color: COLORS.text }}
                />
              </View>

              <View style={s.categoryGrid}>
                {visibleCategories.map(({ key, label }) => (
                  <View key={key} style={[s.categoryCell, isWide && s.categoryCellWide]}>
                    <Chip
                      active={category === key}
                      onPress={() => onPickCategory(key)}
                      style={s.categoryChipCompact}
                      textStyle={s.categoryChipCompactText}
                    >
                      {label}
                    </Chip>
                  </View>
                ))}
              </View>

              {visibleCategories.length === 0 && (
                <View style={s.emptySearchBox}>
                  <Text style={s.emptySearchText}>Kategori tidak ditemukan.</Text>
                </View>
              )}

              {hasHiddenCategories && (
                <TouchableOpacity onPress={() => setShowAllCategories(true)} activeOpacity={0.85} style={s.linkBtn}>
                  <Text style={s.linkBtnText}>Lihat semua kategori</Text>
                </TouchableOpacity>
              )}
              {showAllCategories && filteredCategories.length > 8 && (
                <TouchableOpacity onPress={() => setShowAllCategories(false)} activeOpacity={0.85} style={s.linkBtn}>
                  <Text style={s.linkBtnText}>Tampilkan ringkas</Text>
                </TouchableOpacity>
              )}

              <View style={s.newCategoryBox}>
                <View style={s.newCategoryHead}>
                  <Ionicons name="add-circle-outline" size={16} color="#166534" />
                  <Text style={s.newCategoryTitle}>Tambah Kategori Baru</Text>
                </View>
                <TextInput
                  value={newCategoryLabel}
                  onChangeText={setNewCategoryLabel}
                  placeholder="Nama kategori (contoh: transport_luar)"
                  placeholderTextColor={COLORS.muted}
                  style={s.input}
                />
                <TextInput
                  value={newCategoryKey}
                  onChangeText={setNewCategoryKey}
                  placeholder="Key opsional (a-z, 0-9, underscore)"
                  placeholderTextColor={COLORS.muted}
                  style={[s.input, { marginTop: 8 }]}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity
                  onPress={createCategory}
                  activeOpacity={0.88}
                  disabled={creatingCategory}
                  style={[s.newCategoryBtn, creatingCategory && { opacity: 0.7 }]}
                >
                  {creatingCategory ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <>
                      <Ionicons name="add" size={16} color="#fff" />
                      <Text style={s.newCategoryBtnText}>Simpan Kategori</Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </>
          )}

          {isPesticide && (
            <View style={s.metaCard}>
              <TouchableOpacity onPress={() => setPesticideMetaExpanded((v) => !v)} activeOpacity={0.85} style={s.metaToggleRow}>
                <Text style={s.metaToggleTitle}>Detail Pestisida</Text>
                <Ionicons name={pesticideMetaExpanded ? 'chevron-up' : 'chevron-down'} size={15} color="#166534" />
              </TouchableOpacity>

              {pesticideMetaExpanded && (
                <>
                  <Text style={s.label}>Subkategori</Text>
                  <View style={s.rowWrap}>
                    {PESTICIDE_SUBS.map((sub) => (
                      <Chip key={sub} active={subcategory === sub} onPress={() => setSubcategory(sub)}>
                        {sub}
                      </Chip>
                    ))}
                  </View>

                  <Text style={s.label}>Company (opsional)</Text>
                  <View style={s.rowWrap}>
                    {COMPANY_PRESETS.map((cmp) => (
                      <Chip key={cmp} active={company === cmp} onPress={() => setCompany(cmp)}>
                        {cmp}
                      </Chip>
                    ))}
                  </View>
                  <TextInput
                    value={company}
                    onChangeText={setCompany}
                    placeholder="Isi nama perusahaan jika tidak ada di atas"
                    placeholderTextColor={COLORS.muted}
                    style={s.input}
                  />
                </>
              )}
            </View>
          )}

          {isPupuk && (
            <View style={s.metaCard}>
              <Text style={s.metaToggleTitle}>Jenis Pupuk</Text>
              <Text style={s.metaHint}>
                Pilih supaya analisa pupuk rapi. Kalau kosong, backend tetap membaca dari nama item.
              </Text>
              <View style={s.rowWrap}>
                {PUPUK_SUBS.map((sub) => (
                  <Chip key={sub.key} active={subcategory === sub.key} onPress={() => setSubcategory(sub.key)}>
                    {sub.label}
                  </Chip>
                ))}
              </View>
            </View>
          )}
        </View>

        <View style={s.sectionDivider} />
        <View style={s.sectionCard}>
          <View style={s.sectionTitleRow}>
            <View style={s.sectionIconCircle}>
              <Ionicons name="wallet-outline" size={16} color="#14532d" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.sectionStep}>Langkah 2</Text>
              <Text style={s.sectionTitle}>Detail Nominal</Text>
              <Text style={s.sectionDesc}>Isi tanggal, item, quantity, dan nominal untuk perhitungan otomatis.</Text>
            </View>
          </View>

          <Text style={s.label}>Tanggal</Text>
          <TouchableOpacity onPress={openDatePicker} activeOpacity={0.8} style={[s.input, s.dateInputRow]}>
            <Text style={{ color: date ? COLORS.text : COLORS.muted }}>{date || 'Pilih tanggal'}</Text>
            <Ionicons name="calendar-outline" size={20} color={COLORS.muted} />
          </TouchableOpacity>

          {showDatePicker &&
            (isWeb ? (
              <View style={s.dateWebBox}>
                <DatePicker
                  mode="single"
                  date={dateObj || new Date()}
                  onChange={({ date: d }) => {
                    if (!d) return;
                    const dd = dayjs(d).toDate();
                    setDateObj(dd);
                    setDate(formatYMD(dd));
                    setShowDatePicker(false);
                  }}
                />
              </View>
            ) : (
              <DateTimePicker value={dateObj || new Date()} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'calendar'} onChange={onNativeDateChange} minimumDate={new Date(1970, 0, 1)} />
            ))}

          <Text style={[s.label, { marginTop: 8 }]}>atau ketik manual (YYYY-MM-DD)</Text>
          <TextInput value={date} onChangeText={setDate} placeholder="2026-03-05" placeholderTextColor={COLORS.muted} style={s.input} />

          {isPesticide && (
            <>
              <Text style={s.label}>Pilih Produk (recommended)</Text>

              {selectedPesticide ? (
                <View style={s.selectedBox}>
                  <View style={{ flex: 1 }}>
                    <Text style={s.selectedTitle}>{selectedPesticide.name}</Text>
                    <Text style={s.selectedMeta}>
                      ID: {selectedPesticide.pesticide_id}
                      {selectedPesticide.unit ? ` • Unit: ${selectedPesticide.unit}` : ''}
                      {selectedPesticide.unit_price != null ? ` • Harga: ${formatIDR(selectedPesticide.unit_price)}` : ''}
                      {selectedPesticide.stock != null ? ` • Stok: ${selectedPesticide.stock}` : ''}
                    </Text>
                  </View>
                  <TouchableOpacity onPress={clearSelectedPesticide} style={s.clearBtn} activeOpacity={0.85}>
                    <Ionicons name="close" size={18} color={COLORS.danger} />
                    <Text style={s.clearBtnText}>Ganti</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <>
                  <View style={s.searchInputWrap}>
                    <Ionicons name="search-outline" size={18} color={COLORS.muted} />
                    <TextInput
                      value={pestQuery}
                      onChangeText={setPestQuery}
                      placeholder="Ketik nama produk (min 2 huruf)"
                      placeholderTextColor={COLORS.muted}
                      style={{ flex: 1, color: COLORS.text }}
                    />
                    {pestLoading ? <ActivityIndicator /> : null}
                  </View>

                  {!!pestError && (
                    <View style={[s.hintBox, { backgroundColor: COLORS.dangerSoft, borderColor: 'rgba(159, 33, 56, 0.25)' }]}>
                      <Ionicons name="information-circle-outline" size={16} color={COLORS.danger} />
                      <Text style={[s.hintText, { color: COLORS.danger }]}>{pestError}</Text>
                    </View>
                  )}

                  {pestResults.length > 0 && (
                    <View style={s.dropdown}>
                      {pestResults.map((p) => (
                        <TouchableOpacity key={String(p.pesticide_id)} onPress={() => selectPesticide(p)} style={s.dropdownItem} activeOpacity={0.85}>
                          <Text style={s.dropdownTitle}>{p.name}</Text>
                          <Text style={s.dropdownMeta}>
                            ID: {p.pesticide_id}
                            {p.unit ? ` • ${p.unit}` : ''}
                            {p.unit_price != null ? ` • ${formatIDR(p.unit_price)}` : ''}
                            {p.stock != null ? ` • stok ${p.stock}` : ''}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}

                  <View style={s.hintBox}>
                    <Ionicons name="bulb-outline" size={16} color="#1f2d1f" />
                    <Text style={s.hintText}>Kalau produk belum ada, kamu masih bisa input manual di bawah.</Text>
                  </View>
                </>
              )}
            </>
          )}

          <Text style={s.label}>Nama Item/Deskripsi</Text>
          <TextInput value={itemName} onChangeText={setItemName} placeholder={itemPlaceholder} placeholderTextColor={COLORS.muted} style={s.input} />

          <View style={s.row2}>
            <View style={{ flex: 1 }}>
              <Text style={s.label}>Qty</Text>
              <TextInput value={qty} onChangeText={(t) => setQty(t.replace(/[^0-9.]/g, ''))} keyboardType="numeric" placeholder="1" placeholderTextColor={COLORS.muted} style={s.input} />
            </View>

            <View style={{ width: 12 }} />

            <View style={{ flex: 1 }}>
              <Text style={s.label}>Unit</Text>
              <View style={s.rowWrap}>
                {unitPresets.map((u) => (
                  <Chip key={u} active={unit === u} onPress={() => setUnit(u)}>
                    {u}
                  </Chip>
                ))}
              </View>
              <TextInput value={unit} onChangeText={setUnit} placeholder="Tulis manual jika perlu" placeholderTextColor={COLORS.muted} style={s.input} />
            </View>
          </View>

          <Text style={s.label}>Harga per Unit</Text>
          <View style={s.currencyInputWrap}>
            <Text style={s.currencyPrefix}>Rp</Text>
            <TextInput
              value={price}
              onChangeText={(t) => setPrice(t.replace(/[^0-9.]/g, ''))}
              keyboardType="numeric"
              placeholder="100000"
              placeholderTextColor={COLORS.muted}
              style={s.currencyInput}
            />
          </View>

          <Text style={s.label}>Total</Text>
          <View style={s.currencyInputWrap}>
            <Text style={s.currencyPrefix}>Rp</Text>
            <TextInput
              value={total}
              onChangeText={(t) => setTotal(t.replace(/[^0-9.]/g, ''))}
              keyboardType="numeric"
              placeholder={computedTotal ? `otomatis: ${computedTotal}` : 'isi total atau isi qty + harga'}
              placeholderTextColor={COLORS.muted}
              style={s.currencyInput}
            />
          </View>

          <View style={s.previewBox}>
            <Text style={s.previewLabel}>Preview total</Text>
            <Text style={s.previewAmount}>{formatIDR(finalTotalValue || 0)}</Text>
            <Text style={s.previewHint}>Gunakan total manual jika ada diskon/penyesuaian.</Text>
          </View>
        </View>

        <View style={s.sectionDivider} />
        <View style={s.sectionCard}>
          <View style={s.sectionTitleRow}>
            <View style={s.sectionIconCircle}>
              <Ionicons name="git-network-outline" size={16} color="#14532d" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.sectionStep}>Langkah 3</Text>
              <Text style={s.sectionTitle}>Alokasi Biaya</Text>
              <Text style={s.sectionDesc}>Tentukan biaya untuk musim umum atau bagi langsung ke lahan.</Text>
            </View>
          </View>

          <View style={s.allocModeGrid}>
            <TouchableOpacity
              activeOpacity={0.9}
              onPress={() => {
                setAssignMode('season');
                setSelectedFields([]);
                setFieldQuery('');
              }}
              style={[s.allocModeCard, assignMode === 'season' && s.allocModeCardActive]}
            >
              <Ionicons name="albums-outline" size={16} color={assignMode === 'season' ? COLORS.accent : COLORS.muted} />
              <Text style={[s.allocModeTitle, assignMode === 'season' && s.allocModeTitleActive]}>Umum Musim</Text>
              <Text style={s.allocModeDesc}>Satu expense untuk ringkasan musim.</Text>
            </TouchableOpacity>

            <TouchableOpacity
              activeOpacity={0.9}
              onPress={() => {
                setAssignMode('fields');
                setField('');
              }}
              style={[s.allocModeCard, assignMode === 'fields' && s.allocModeCardActive]}
            >
              <Ionicons name="git-network-outline" size={16} color={assignMode === 'fields' ? COLORS.accent : COLORS.muted} />
              <Text style={[s.allocModeTitle, assignMode === 'fields' && s.allocModeTitleActive]}>Pilih Lahan</Text>
              <Text style={s.allocModeDesc}>Biaya langsung teralokasi ke tiap lahan.</Text>
            </TouchableOpacity>
          </View>

          {assignMode === 'fields' && (
            <View style={s.allocBox}>
              <View style={s.allocHeader}>
                <Text style={s.allocTitle}>Pilih Lahan</Text>
                <TouchableOpacity onPress={fetchFieldsCompat} activeOpacity={0.85} style={s.refreshMiniBtn}>
                  {fieldsLoading ? <ActivityIndicator /> : <Ionicons name="refresh" size={16} color={COLORS.accent} />}
                  <Text style={s.refreshMiniText}>Refresh</Text>
                </TouchableOpacity>
              </View>

              <View style={s.allocStatsRow}>
                <View style={s.allocStatBox}>
                  <Text style={s.allocStatLabel}>Terpilih</Text>
                  <Text style={s.allocStatValue}>{selectedFields.length} lahan</Text>
                </View>
                <View style={s.allocStatDivider} />
                <View style={s.allocStatBox}>
                  <Text style={s.allocStatLabel}>Total Rante</Text>
                  <Text style={s.allocStatValue}>{Number(selectedAreaTotal || 0).toFixed(2)}</Text>
                </View>
              </View>

              <View style={s.allocToolsRow}>
                <View style={s.allocSearchWrap}>
                  <Ionicons name="search-outline" size={16} color={COLORS.muted} />
                  <TextInput
                    value={fieldQuery}
                    onChangeText={setFieldQuery}
                    placeholder="Cari lahan..."
                    placeholderTextColor={COLORS.muted}
                    style={s.allocSearchInput}
                  />
                </View>
                <View style={s.allocActionsRow}>
                  <TouchableOpacity onPress={selectAllFilteredFields} activeOpacity={0.85} style={s.allocToolBtn}>
                    <Text style={s.allocToolBtnText}>Pilih Semua</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={clearSelectedFields} activeOpacity={0.85} style={s.allocToolBtnGhost}>
                    <Text style={s.allocToolBtnGhostText}>Reset</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {fieldsLoading ? (
                <View style={{ paddingVertical: 10 }}>
                  <ActivityIndicator />
                </View>
              ) : filteredFieldsList.length === 0 ? (
                <View style={s.emptyFields}>
                  <Text style={{ fontWeight: '900', color: COLORS.text }}>Lahan tidak ditemukan</Text>
                  <Text style={{ color: COLORS.muted, fontWeight: '700', marginTop: 4 }}>
                    Pastikan endpoint fields sudah ada. Minimal return: id, name, area_rante.
                  </Text>
                </View>
              ) : (
                filteredFieldsList.map((f) => {
                  const checked = selectedFields.includes(f.id);
                  return (
                    <TouchableOpacity key={f.id} style={[s.fieldRow, checked && s.fieldRowActive]} onPress={() => toggleFieldPick(f.id)} activeOpacity={0.9}>
                      <View style={{ flex: 1 }}>
                        <Text style={s.fieldName}>{f.name}</Text>
                        <Text style={s.fieldMeta}>{f.area_rante != null ? `${f.area_rante} rante` : 'rante: belum diisi'}</Text>
                      </View>
                      <Ionicons name={checked ? 'checkmark-circle' : 'ellipse-outline'} size={22} color={checked ? COLORS.accent : PADIPRO.mutedSoft} />
                    </TouchableOpacity>
                  );
                })
              )}

              <Text style={[s.label, { marginTop: 12 }]}>Metode Pembagian</Text>
              <View style={s.rowWrap}>
                <Chip active={splitMethod === 'area'} onPress={() => setSplitMethod('area')}>
                  Otomatis (luas)
                </Chip>
                <Chip active={splitMethod === 'equal'} onPress={() => setSplitMethod('equal')}>
                  Rata
                </Chip>
              </View>

              {splitPreview.rows.length > 0 && (
                <View style={s.splitPreviewBox}>
                  <Text style={s.splitPreviewTitle}>Preview Pembagian</Text>
                  <View style={s.splitHeadGrid}>
                    <View style={s.splitHeadItem}>
                      <Text style={s.splitHeadLabel}>Total expense</Text>
                      <Text style={s.splitHeadValue}>{formatIDR(finalTotalValue || 0)}</Text>
                    </View>
                    <View style={s.splitHeadItem}>
                      <Text style={s.splitHeadLabel}>Jumlah lahan</Text>
                      <Text style={s.splitHeadValue}>{selectedFieldObjs.length}</Text>
                    </View>
                    {splitPreview.methodUsed === 'area' ? (
                      <>
                        <View style={s.splitHeadItem}>
                          <Text style={s.splitHeadLabel}>Total rante</Text>
                          <Text style={s.splitHeadValue}>{formatRante(splitPreview.denominator)}</Text>
                        </View>
                        <View style={s.splitHeadItem}>
                          <Text style={s.splitHeadLabel}>Biaya per rante</Text>
                          <Text style={s.splitHeadValue}>{formatIDR(splitPreview.unitCost)}</Text>
                        </View>
                      </>
                    ) : (
                      <View style={s.splitHeadItemFull}>
                        <Text style={s.splitHeadLabel}>Biaya per lahan (rata)</Text>
                        <Text style={s.splitHeadValue}>{formatIDR(splitPreview.unitCost)}</Text>
                      </View>
                    )}
                  </View>

                  {splitPreview.rows.map((x) => (
                    <View key={x.id} style={s.splitRow}>
                      <View style={{ flex: 1, paddingRight: 8 }}>
                        <Text style={s.splitName} numberOfLines={1}>{x.name}</Text>
                        {splitPreview.methodUsed === 'area' ? (
                          <Text style={s.splitMeta}>{formatRante(x.area)} / {formatRante(splitPreview.denominator)} rante</Text>
                        ) : (
                          <Text style={s.splitMeta}>1 / {selectedFieldObjs.length} bagian</Text>
                        )}
                      </View>
                      <Text style={s.splitAmt}>{formatIDR(x.amount)}</Text>
                    </View>
                  ))}
                  <View style={s.splitTotalRow}>
                    <Text style={s.splitTotalLabel}>Total hasil pembagian (preview)</Text>
                    <Text style={s.splitTotalAmt}>{formatIDR(splitPreview.totalAllocated)}</Text>
                  </View>
                  <Text style={s.splitAssistText}>Harus sama dengan total expense agar tidak double hitung.</Text>
                  <View style={s.splitTotalRow}>
                    <Text style={s.splitTotalLabel}>Selisih vs total expense</Text>
                    <Text style={s.splitDeltaAmt}>
                      {formatSignedIDR(splitPreview.totalAllocated - Number(finalTotalValue || 0))}
                    </Text>
                  </View>
                  {splitPreview.methodUsed === 'area' && !!splitPreview.invalidAreaCount && (
                    <Text style={s.splitWarnText}>
                      {splitPreview.invalidAreaCount} lahan belum punya rante valid, pembagian sementara rata.
                    </Text>
                  )}
                </View>
              )}
            </View>
          )}

          {assignMode === 'season' && (
            <>
              <Text style={s.label}>Ladang/Plot (opsional)</Text>
              <TextInput value={field} onChangeText={setField} placeholder="Plot A" placeholderTextColor={COLORS.muted} style={s.input} />
            </>
          )}
        </View>

        <View style={s.sectionDivider} />
        <View style={s.sectionCard}>
          <View style={s.sectionTitleRow}>
            <View style={s.sectionIconCircle}>
              <Ionicons name="document-text-outline" size={16} color="#14532d" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.sectionStep}>Langkah 4</Text>
              <Text style={s.sectionTitle}>Catatan</Text>
              <Text style={s.sectionDesc}>Tambahkan detail tambahan jika diperlukan.</Text>
            </View>
          </View>
          <Text style={s.label}>Catatan (opsional)</Text>
          <TextInput value={notes} onChangeText={setNotes} placeholder="Keterangan tambahan" placeholderTextColor={COLORS.muted} style={[s.input, { height: 90 }]} multiline />
          <View style={s.tipsBox}>
            <Ionicons name="sparkles-outline" size={16} color="#14532d" />
            <Text style={s.tipsText}>Tips: untuk biaya per lahan, pilih mode “Pilih Lahan”.</Text>
          </View>
        </View>

        </ScrollView>

        <View style={[s.submitBar, { paddingHorizontal: contentPadding, paddingBottom: 14 + bottomInset }]}>
          <TouchableOpacity onPress={submit} activeOpacity={0.9} style={[s.btnPrimary, s.btnPrimaryInBar, loading && { opacity: 0.7 }]} disabled={loading}>
            {loading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <View style={s.btnPrimaryRow}>
                <Ionicons name="save-outline" size={18} color="#fff" />
                <Text style={[s.btnPrimaryText, { marginLeft: 8 }]}>Simpan Pengeluaran</Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: COLORS.bg,
  },
  screenBody: {
    flex: 1,
  },
  formScroll: {
    flex: 1,
  },
  pageContent: {
    paddingHorizontal: R.pad,
    paddingTop: 14,
    paddingBottom: 112,
  },
  headerWrap: {
    marginBottom: 10,
  },

  infoBox: {
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 10,
    borderWidth: 1,
  },
  warnBox: { backgroundColor: PADIPRO.warningSoft, borderColor: PADIPRO.warning },
  okBox: { backgroundColor: PADIPRO.primarySoft, borderColor: PADIPRO.primary },
  infoText: { color: COLORS.text, fontWeight: '700', marginHorizontal: 8 },
  forceBtn: {
    borderWidth: 1,
    borderColor: COLORS.accent,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: PADIPRO.primarySoft,
  },
  forceBtnText: { color: COLORS.accent, fontWeight: '700', fontSize: 12 },
  summaryStrip: {
    marginTop: 2,
    marginBottom: 2,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: R.radius,
    backgroundColor: PADIPRO.surfaceSunken,
    paddingVertical: 10,
    paddingHorizontal: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  summaryItem: {
    flex: 1,
    paddingHorizontal: 8,
  },
  summaryLabel: {
    color: COLORS.muted,
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.45,
  },
  summaryValue: {
    marginTop: 4,
    color: COLORS.text,
    fontWeight: '900',
    fontSize: 13,
  },
  summaryDivider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: PADIPRO.primarySoft,
  },

  sectionDivider: {
    height: 1,
    marginHorizontal: 1,
    marginTop: 10,
    marginBottom: 2,
    backgroundColor: PADIPRO.primarySoft,
  },
  sectionCard: {
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.card,
    borderRadius: R.radius,
    padding: 14,
    marginTop: 8,
    ...padiproShadow,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 2,
  },
  sectionIconCircle: {
    width: 29,
    height: 29,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.primarySoft,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    marginTop: 1,
  },
  sectionStep: {
    color: COLORS.accent,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  sectionTitle: {
    color: COLORS.text,
    fontSize: 17,
    fontWeight: '900',
    marginTop: 3,
  },
  sectionDesc: {
    marginTop: 4,
    color: COLORS.muted,
    fontWeight: '600',
    lineHeight: 18,
  },

  label: {
    fontWeight: '800',
    marginTop: 12,
    marginBottom: 7,
    color: COLORS.text,
    letterSpacing: 0.2,
  },
  input: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 12,
    padding: 12,
    backgroundColor: PADIPRO.surfaceSunken,
    color: COLORS.text,
  },
  currencyInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 12,
    backgroundColor: PADIPRO.surfaceSunken,
    paddingHorizontal: 12,
  },
  currencyPrefix: {
    color: COLORS.text,
    fontWeight: '800',
    marginRight: 8,
  },
  currencyInput: {
    flex: 1,
    color: COLORS.text,
    paddingVertical: 12,
  },

  dateInputRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  dateWebBox: {
    marginTop: 8,
    backgroundColor: COLORS.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    overflow: 'hidden',
  },

  rowWrap: { flexDirection: 'row', flexWrap: 'wrap' },
  chip: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: COLORS.card,
    marginRight: 8,
    marginBottom: 8,
  },
  chipActive: {
    borderColor: COLORS.accent,
    backgroundColor: PADIPRO.primarySoft,
    shadowColor: COLORS.accent,
    shadowOpacity: 0.06,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
  },
  chipText: { color: COLORS.text, fontWeight: '700' },
  chipTextActive: { color: COLORS.accent },
  categoryTopBar: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
  },
  selectionBadge: {
    borderRadius: 999,
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: PADIPRO.surfaceSunken,
    borderWidth: 1,
    borderColor: PADIPRO.border,
  },
  selectionBadgeText: {
    color: COLORS.text,
    fontWeight: '700',
    fontSize: 12,
  },
  compactToggleBtn: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.primarySoft,
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  compactToggleText: {
    color: COLORS.accent,
    fontWeight: '700',
    fontSize: 12,
    marginRight: 4,
  },
  categoryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
    marginHorizontal: -4,
  },
  categoryCell: {
    width: '50%',
    paddingHorizontal: 4,
    marginBottom: 8,
  },
  categoryCellWide: { width: '25%' },
  categoryChipCompact: {
    marginRight: 0,
    marginBottom: 0,
    borderRadius: 11,
    paddingVertical: 9,
    justifyContent: 'center',
    backgroundColor: PADIPRO.surfaceSunken,
  },
  categoryChipCompactText: {
    fontSize: 12,
    textTransform: 'capitalize',
  },
  emptySearchBox: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surfaceSunken,
    borderRadius: 10,
    padding: 10,
    marginTop: 6,
  },
  emptySearchText: {
    color: COLORS.muted,
    fontWeight: '700',
    fontSize: 12,
  },
  linkBtn: {
    alignSelf: 'flex-start',
    marginTop: 8,
    paddingVertical: 4,
  },
  linkBtnText: {
    color: COLORS.accent,
    fontWeight: '800',
    fontSize: 12,
    textDecorationLine: 'underline',
  },
  newCategoryBox: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 12,
    backgroundColor: PADIPRO.surfaceSunken,
    padding: 10,
  },
  newCategoryHead: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  newCategoryTitle: {
    marginLeft: 6,
    color: COLORS.text,
    fontWeight: '800',
  },
  newCategoryBtn: {
    marginTop: 10,
    borderRadius: 10,
    backgroundColor: COLORS.accent,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  newCategoryBtnText: {
    marginLeft: 6,
    color: '#fff',
    fontWeight: '900',
  },
  metaCard: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 12,
    backgroundColor: PADIPRO.surfaceSunken,
    padding: 10,
    marginTop: 10,
  },
  metaToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  metaToggleTitle: {
    color: COLORS.text,
    fontWeight: '800',
    fontSize: 13,
  },
  metaHint: {
    marginTop: 6,
    marginBottom: 8,
    color: COLORS.muted,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 17,
  },

  row2: { flexDirection: 'row', alignItems: 'flex-start', marginTop: 2 },

  previewBox: {
    marginTop: 12,
    borderRadius: 14,
    padding: 12,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  previewLabel: {
    color: COLORS.text,
    fontWeight: '700',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  previewAmount: {
    marginTop: 4,
    color: PADIPRO.primaryDark,
    fontWeight: '900',
    fontSize: 24,
    letterSpacing: 0.3,
  },
  previewHint: {
    marginTop: 4,
    color: COLORS.muted,
    fontSize: 12,
    fontWeight: '600',
  },

  hintBox: {
    marginTop: 8,
    padding: 10,
    borderRadius: 12,
    backgroundColor: PADIPRO.surfaceSunken,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    flexDirection: 'row',
    alignItems: 'center',
  },
  hintText: { flex: 1, color: COLORS.text, fontWeight: '600', marginLeft: 8 },

  selectedBox: {
    borderWidth: 1,
    borderColor: PADIPRO.primary,
    backgroundColor: PADIPRO.primarySoft,
    borderRadius: 12,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  selectedTitle: { color: COLORS.text, fontWeight: '900', fontSize: 14 },
  selectedMeta: { color: COLORS.muted, fontWeight: '700', marginTop: 4, fontSize: 12 },

  clearBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(159, 33, 56, 0.25)',
    backgroundColor: COLORS.card,
    marginLeft: 10,
  },
  clearBtnText: { color: PADIPRO.danger, fontWeight: '800', marginLeft: 6 },

  searchInputWrap: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: COLORS.card,
    flexDirection: 'row',
    alignItems: 'center',
  },

  dropdown: { marginTop: 8, borderWidth: 1, borderColor: PADIPRO.border, borderRadius: 12, overflow: 'hidden', backgroundColor: '#fff' },
  dropdownItem: { padding: 12, borderBottomWidth: 1, borderBottomColor: PADIPRO.border },
  dropdownTitle: { color: COLORS.text, fontWeight: '900' },
  dropdownMeta: { color: COLORS.muted, fontWeight: '700', marginTop: 4, fontSize: 12 },

  allocModeGrid: {
    marginTop: 4,
    flexDirection: 'row',
    columnGap: 8,
  },
  allocModeCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 12,
    backgroundColor: PADIPRO.surfaceSunken,
    padding: 10,
  },
  allocModeCardActive: {
    borderColor: COLORS.accent,
    backgroundColor: PADIPRO.primarySoft,
  },
  allocModeTitle: {
    marginTop: 6,
    color: COLORS.text,
    fontWeight: '900',
  },
  allocModeTitleActive: { color: COLORS.accent },
  allocModeDesc: {
    marginTop: 4,
    color: COLORS.muted,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 17,
  },

  allocBox: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 12,
    padding: 12,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  allocHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  allocTitle: { fontWeight: '900', color: COLORS.text, fontSize: 14 },
  allocStatsRow: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 12,
    backgroundColor: PADIPRO.surfaceSunken,
    paddingVertical: 8,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  allocStatBox: { flex: 1 },
  allocStatLabel: {
    color: COLORS.muted,
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.45,
  },
  allocStatValue: {
    marginTop: 2,
    color: COLORS.text,
    fontWeight: '900',
  },
  allocStatDivider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: PADIPRO.border,
    marginHorizontal: 8,
  },
  allocToolsRow: {
    marginTop: 10,
    flexDirection: 'column',
    alignItems: 'stretch',
    rowGap: 8,
  },
  allocSearchWrap: {
    flex: 1,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 10,
    backgroundColor: COLORS.card,
    paddingHorizontal: 10,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  allocSearchInput: {
    flex: 1,
    marginLeft: 6,
    color: COLORS.text,
    fontSize: 13,
  },
  allocToolBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: PADIPRO.primary,
    backgroundColor: PADIPRO.primarySoft,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 7,
    alignItems: 'center',
  },
  allocToolBtnText: {
    color: COLORS.accent,
    fontSize: 11,
    fontWeight: '800',
  },
  allocToolBtnGhost: {
    flex: 1,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: COLORS.card,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 7,
    alignItems: 'center',
  },
  allocToolBtnGhostText: {
    color: COLORS.muted,
    fontSize: 11,
    fontWeight: '800',
  },
  allocActionsRow: {
    flexDirection: 'row',
    columnGap: 8,
  },

  refreshMiniBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: PADIPRO.primary,
    backgroundColor: PADIPRO.primarySoft,
  },
  refreshMiniText: { color: COLORS.accent, fontWeight: '900', marginLeft: 6 },

  emptyFields: { marginTop: 10, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: PADIPRO.border, backgroundColor: PADIPRO.surfaceSunken },

  fieldRow: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: COLORS.card,
    flexDirection: 'row',
    alignItems: 'center',
  },
  fieldRowActive: { borderColor: COLORS.accent, backgroundColor: PADIPRO.primarySoft },
  fieldName: { fontWeight: '900', color: COLORS.text },
  fieldMeta: { marginTop: 2, color: COLORS.muted, fontWeight: '700', fontSize: 12 },

  splitPreviewBox: { marginTop: 12, borderRadius: 12, borderWidth: 1, borderColor: PADIPRO.border, backgroundColor: PADIPRO.surfaceSunken, padding: 12 },
  splitPreviewTitle: { fontWeight: '900', color: COLORS.text },
  splitHeadGrid: {
    marginTop: 8,
    rowGap: 8,
  },
  splitHeadItem: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 10,
    backgroundColor: PADIPRO.surfaceSunken,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  splitHeadItemFull: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 10,
    backgroundColor: PADIPRO.surfaceSunken,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  splitHeadLabel: { color: COLORS.muted, fontWeight: '700', fontSize: 11 },
  splitHeadValue: { marginTop: 2, color: COLORS.accent, fontWeight: '900', fontSize: 14 },
  splitRow: { marginTop: 8, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  splitName: { flex: 1, color: COLORS.text, fontWeight: '800', paddingRight: 8 },
  splitMeta: { marginTop: 2, color: COLORS.muted, fontWeight: '700', fontSize: 11 },
  splitAmt: { color: COLORS.text, fontWeight: '900' },
  splitTotalRow: {
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: PADIPRO.border,
    paddingTop: 7,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  splitTotalLabel: { color: COLORS.text, fontWeight: '800', fontSize: 12 },
  splitTotalAmt: { color: COLORS.accent, fontWeight: '900' },
  splitDeltaAmt: { color: COLORS.text, fontWeight: '900' },
  splitAssistText: { marginTop: 4, color: COLORS.muted, fontSize: 11, fontWeight: '700' },
  splitWarnText: {
    marginTop: 8,
    color: PADIPRO.warning,
    fontSize: 12,
    fontWeight: '700',
  },

  tipsBox: {
    marginTop: 12,
    padding: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: '#eff9f1',
    flexDirection: 'row',
    alignItems: 'center',
  },
  tipsText: {
    flex: 1,
    marginLeft: 8,
    color: COLORS.accent,
    fontWeight: '700',
    lineHeight: 18,
  },

  btnPrimary: {
    backgroundColor: COLORS.accent,
    borderRadius: 14,
    paddingVertical: 15,
    alignItems: 'center',
    ...padiproShadowMd,
  },
  submitBar: {
    paddingHorizontal: R.pad,
    paddingTop: 10,
    paddingBottom: Platform.OS === 'ios' ? 20 : 12,
    borderTopWidth: 1,
    borderTopColor: PADIPRO.border,
    backgroundColor: PADIPRO.surfaceSunken,
    shadowColor: '#10291b',
    shadowOpacity: 0.06,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: -2 },
    elevation: 2,
  },
  btnPrimaryInBar: {
    marginTop: 0,
  },
  btnPrimaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  btnPrimaryText: { color: '#fff', fontWeight: '900', letterSpacing: 0.25, fontSize: 16 },
});
